import { normalizeProfileUserId } from './profileRoutes';

const ALLOWED_STATUSES = new Set(['online', 'offline', 'away']);

export const isSafePresenceEpoch = (value) => (
    Number.isSafeInteger(value) && value > 0
);

/**
 * Normalize a presence record at a client trust boundary.
 *
 * Local/bootstrap callers may omit an epoch. Network callers set
 * `requireEpoch` so malformed ordering data is rejected instead of silently
 * becoming an unversioned update.
 */
export const normalizePresenceStatus = (value, { requireEpoch = false } = {}) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const userId = normalizeProfileUserId(value.userId);
    if (!userId || value.userId !== userId) {
        return null;
    }

    const hasStatus = value.status !== undefined;
    const rawStatus = typeof value.status === 'string'
        ? value.status.trim().toLowerCase()
        : undefined;
    if (hasStatus && (!rawStatus || !ALLOWED_STATUSES.has(rawStatus))) {
        return null;
    }
    if (value.online !== undefined && typeof value.online !== 'boolean') {
        return null;
    }
    const status = hasStatus
        ? rawStatus
        : (typeof value.online === 'boolean' ? (value.online ? 'online' : 'offline') : undefined);
    if (!status) {
        return null;
    }
    if (typeof value.online === 'boolean' && value.online !== (status === 'online')) {
        return null;
    }

    if (value.lastSeen !== undefined
        && value.lastSeen !== null
        && (typeof value.lastSeen !== 'string' || !value.lastSeen.trim())) {
        return null;
    }

    const hasEpoch = value.epoch !== undefined;
    if ((requireEpoch && !hasEpoch) || (hasEpoch && !isSafePresenceEpoch(value.epoch))) {
        return null;
    }

    return {
        userId,
        online: status === 'online',
        lastSeen: typeof value.lastSeen === 'string' && value.lastSeen.trim()
            ? value.lastSeen
            : null,
        status,
        ...(hasEpoch ? { epoch: value.epoch } : {}),
    };
};
